// authApi.js
// Authentication service module using Firebase Authentication
// Handles user authentication with Firebase and syncs with backend Firestore
// All password management is handled by Firebase Auth
// User profile data is stored in Firestore via backend API

import { 
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { auth } from '../firebase';
import apiRequest from './api';

// Persist auth session in both sessionStorage and localStorage.
// sessionStorage supports tab-scoped UX, localStorage enables recovery after hard refresh.
function persistAuthSession(userData) {
  const payload = {
    userId: userData.userId,
    firebaseUid: userData.firebaseUid,
    userEmail: userData.email,
    isAdmin: userData.isAdmin?.toString?.() ?? 'false',
  };

  Object.entries(payload).forEach(([key, value]) => {
    if (value != null) {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    }
  });
}

// Clear all local auth session keys from browser storage.
export function clearAuthSession() {
  ['userId', 'firebaseUid', 'userEmail', 'isAdmin'].forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
}

// Re-sync Firestore profile from active Firebase session.
// Useful when browser storage is missing but Firebase Auth is still authenticated.
export async function syncUserFromFirebaseSession(firebaseUser) {
  if (!firebaseUser) {
    throw new Error('No Firebase user available for session sync');
  }

  const idToken = await firebaseUser.getIdToken(true);
  const userData = await apiRequest('/Auth/verify-token', {
    method: 'POST',
    body: JSON.stringify({ idToken }),
  });

  persistAuthSession(userData);

  return {
    id: userData.userId,
    ...userData,
  };
}

// Authenticates user with Firebase and gets/creates Firestore profile
// Stores user data in sessionStorage
// Returns complete user profile from Firestore
export async function login(email, password) {
  try {
    // Sign in with Firebase Authentication
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    // Get Firebase ID token
    const idToken = await firebaseUser.getIdToken();
    
    // Verify token with backend and get/create Firestore profile
    const userData = await apiRequest('/Auth/verify-token', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
    
    // Store user data in browser storage for session recovery
    persistAuthSession(userData);
    
    // Return user object with proper id field mapping
    return {
      id: userData.userId,
      ...userData
    };
  } catch (error) {
    throw new Error(error.message || 'Login failed');
  }
}


// Logs out current user from Firebase
// Clears local session storage
export async function logout() {
  try {
    await signOut(auth);
    clearAuthSession();
  } catch (error) {
    throw new Error('Logout failed');
  }
}

// Fetches current authenticated user data from backend using Firebase token
// Requires Firebase authentication
// Returns full user object with profile information
export async function getCurrentUser() {
  try {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('Not authenticated');
    }
    
    const token = await firebaseUser.getIdToken();
    const userData = await apiRequest('/Auth/me', {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });
    
    // Normalize id field regardless of server casing
    return {
      id: userData.id || userData.userId,
      ...userData
    };
  } catch (error) {
    throw error;
  }
}

// Gets the current Firebase ID token for authenticated requests
// Returns null if user is not authenticated
export async function getAuthToken() {
  try {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;
    
    return await firebaseUser.getIdToken();
  } catch (error) {
    console.error('Get auth token error:', error);
    return null;
  }
}
